<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'accountsDailyFlow',
    path: '/accounts-daily-flow',
    component: 'AccountsDailyFlowView',
    title: () => pgettext('AccountsDailyFlowView', 'Daily Flow'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AccountsDailyFlowView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getItems = (...args) => {
    const _parameters = parameters.value;
    return $fn.value.fetch('browse_accounts_daily_flow', {
      parameters: {
        ..._parameters,
        account: _parameters.account.id,
      },
    });
  };

  const selectedItems = ref([]);
  const accounts = ref(null);
  const parameters = ref({
    date_from: null,
    date_to: null,
    account: accounts.value ? accounts.value[0] : null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_reorderableColumns = computed(() => false);
  const b_2248226175642056_columnDefaults = computed(() => ({
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'date',
      name: $fn.value.pgettext('AccountsDailyFlowView', 'Date'),
    },
    {
      field: 'open',
      name: $fn.value.pgettext('AccountsDailyFlowView', 'Opening balance'),
      type: 'number',
      noFooterTotals: true,
      formatter: 'number',
    },
    {
      field: 'pay_in',
      name: $fn.value.pgettext('AccountsDailyFlowView', 'Pay in'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'pay_out',
      name: $fn.value.pgettext('AccountsDailyFlowView', 'Pay out'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'close',
      name: $fn.value.pgettext('AccountsDailyFlowView', 'Closing balance'),
      type: 'number',
      noFooterTotals: true,
      formatter: 'number',
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    accounts: accounts.value,
    parameters: parameters.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_reorderableColumns:
      b_2248226175642056_reorderableColumns.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_accounts = () => {
    return $fn.value
      .fetch('list_account')
      .then((resp) =>
        _.orderBy(
          resp.data,
          ['inactive', 'account_type', 'name'],
          ['asc', 'desc', 'asc']
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'accounts' },
      {
        name: 'parameters',
        cache: { type: 'local', key: 'AccountsDailyFlowViewFilters' },
        dependsOn: ['accounts'],
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_2248226175642056_reorderableColumns',
        nodeId: '2248226175642056',
      },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { accounts: $$load_accounts },
    ux: {},
    dataRefs: {
      selectedItems,
      accounts,
      parameters,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_reorderableColumns,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('AccountsDailyFlowView', 'Daily Flow'),
      icon: $config.value.accountsDailyFlow.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_01ik0rxoj_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_8m1g2vfcr_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_ufuinucd7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Account',
      props: {
        oid: $event.data.data.account_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_rbrmcy5c1_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'account_name';
  };
  const act_2095089031469157_setData_4ph35qlul_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'parameters.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2095089031469157_setData_8md5ax93n_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'parameters.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_9004123373732307_reloadGrid_6y12yj2vb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'accountsDailyFlow',
    };
  };

  const act_7315092382398562_closeModal_01ik0rxoj =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_01ik0rxoj_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hqyrtjw6u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_01ik0rxoj,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hqyrtjw6u],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e662yqjo8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_afqtoevha =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e662yqjo8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_afqtoevha],
    event: 'reload',
  });
  const act_2248226175642056_setData_8m1g2vfcr = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_8m1g2vfcr_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_7fks58urb =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_8m1g2vfcr,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_7fks58urb],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_ufuinucd7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_ufuinucd7_getActionArgs,
    });
  const inst_evh_2248226175642056_command_rbrmcy5c1 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_ufuinucd7,
      event: 'command',
      when: inst_evh_2248226175642056_command_rbrmcy5c1_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_rbrmcy5c1],
    event: 'command',
  });
  const act_2095089031469157_setData_4ph35qlul = new actionTypes.SetDataAction({
    actionArgs: act_2095089031469157_setData_4ph35qlul_getActionArgs,
  });
  const inst_evh_2095089031469157_changed_tsx3h2q6d =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_setData_4ph35qlul,
      event: 'changed',
      displayName: 'setData',
    });
  const act_2095089031469157_setData_8md5ax93n = new actionTypes.SetDataAction({
    actionArgs: act_2095089031469157_setData_8md5ax93n_getActionArgs,
  });
  const inst_evh_2095089031469157_changed_7sp0953ej =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_setData_8md5ax93n,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_2095089031469157_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2095089031469157_changed_tsx3h2q6d,
      inst_evh_2095089031469157_changed_7sp0953ej,
    ],
    event: 'changed',
  });
  const act_9004123373732307_reloadGrid_6y12yj2vb =
    new actionTypes.ReloadGridAction({
      actionArgs: act_9004123373732307_reloadGrid_6y12yj2vb_getActionArgs,
      displayName: 'load',
    });
  const inst_evh_9004123373732307_click_edv123gie =
    new actionTypes.EventHandlerImpl({
      action: act_9004123373732307_reloadGrid_6y12yj2vb,
      event: 'click',
      displayName: 'load',
    });
  const inst_evh_9004123373732307_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_9004123373732307_click_edv123gie],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_hqyrtjw6u,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_afqtoevha,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_7fks58urb,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_rbrmcy5c1,
    inst_evh_2248226175642056_command,
    inst_evh_2095089031469157_changed_tsx3h2q6d,
    inst_evh_2095089031469157_changed_7sp0953ej,
    inst_evh_2095089031469157_changed,
    inst_evh_9004123373732307_click_edv123gie,
    load: inst_evh_9004123373732307_click_edv123gie,
    inst_evh_9004123373732307_click,
  };

  const evh_7315092382398562_close_hqyrtjw6u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hqyrtjw6u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_afqtoevha = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_afqtoevha.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_7fks58urb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_7fks58urb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_rbrmcy5c1 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_rbrmcy5c1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_changed_tsx3h2q6d = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_changed_tsx3h2q6d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_changed_7sp0953ej = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_changed_7sp0953ej.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_changed = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click_edv123gie = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click_edv123gie.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9004123373732307_click = async (event, scope) => {
    return await actions.inst_evh_9004123373732307_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AccountsDailyFlowView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="accountsDailyFlow"
      :headerTitle="pgettext('AccountsDailyFlowView', 'Daily Flow')"
      :items="getItems"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'accounts-daily-flow')"
      footer-totals
      :reorderableColumns="b_2248226175642056_reorderableColumns"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <UcAccountSelector v-model="parameters.account"> </UcAccountSelector>
          <DateRangeSelector
            name="paymentFilters"
            :from="parameters.date_from"
            :to="parameters.date_to"
            @changed="evh_2095089031469157_changed($event, {})"
          >
          </DateRangeSelector>
          <BtnLoad
            :action="actions.load"
            @click="evh_9004123373732307_click($event, {})"
          >
          </BtnLoad>
          <PageHelp path="/finance/accounts-daily-flow.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
